import { useMemo, useState } from 'react';
import { CheckCircle2, AlertTriangle, CircleDashed, Info, Loader2, Trash2 } from 'lucide-react';
import { INTEGRATION_HELP, IntegrationHelpModal } from './IntegrationHelpModal';
import {
  deleteIntegration,
  saveIntegration,
  testIntegration,
  type ConfigInput,
  type IntegrationStatus,
  type SettingsIntegration,
} from '../../lib/settingsApi';
import { ApiError } from '../../lib/api';
import { Select } from '../ui/Select';
import { useConfirm } from '../ui/ConfirmProvider';

const MASK = '••••••••';

const STATUS_STYLES: Record<
  IntegrationStatus,
  {
    container: string;
    iconWrap: string;
    badge: string;
    label: string;
    icon: typeof CheckCircle2;
    iconClass: string;
  }
> = {
  connected: {
    // Keep the card on the neutral (theme-aware) surface; only the badge is
    // tinted green. The static `green-*` palette does NOT flip with dark mode,
    // so using it as a card background renders as a bright light-green block.
    container: 'border-gray-200 bg-card',
    iconWrap: 'bg-emerald-500/15 text-emerald-500',
    badge: 'border-emerald-500/30 bg-emerald-500/15 text-emerald-400',
    label: 'Conectat',
    icon: CheckCircle2,
    iconClass: 'text-emerald-500',
  },
  configured: {
    container: 'border-gray-200 bg-card',
    iconWrap: 'bg-emerald-500/15 text-emerald-500',
    badge: 'border-emerald-500/30 bg-emerald-500/15 text-emerald-400',
    label: 'Configurat',
    icon: CheckCircle2,
    iconClass: 'text-emerald-500',
  },
  not_configured: {
    container: 'border-gray-200 bg-card',
    iconWrap: 'bg-gray-100 text-gray-400',
    badge: 'border-gray-200 bg-gray-100 text-gray-500',
    label: 'Neconfigurat',
    icon: CircleDashed,
    iconClass: 'text-gray-400',
  },
  error: {
    container: 'border-gray-200 bg-card',
    iconWrap: 'bg-red-500/15 text-red-500',
    badge: 'border-red-500/30 bg-red-500/15 text-red-400',
    label: 'Eroare',
    icon: AlertTriangle,
    iconClass: 'text-red-500',
  },
};

function formatTimestamp(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('ro-RO');
}

/**
 * One integration card: status badge, credential inputs, Test Connection + Save.
 * Secret fields are pre-filled with the mask when a value is stored; clearing
 * the mask and leaving it blank preserves the stored secret on save.
 */
export function IntegrationCard({
  integration,
  onChanged,
  companyId,
}: {
  integration: SettingsIntegration;
  onChanged: (updated: SettingsIntegration) => void;
  /** Firm scope for per-company integrations (SmartBill). undefined = default firm. */
  companyId?: string;
}) {
  // Form state seeded from the masked server values.
  const [values, setValues] = useState<Record<string, string>>(() => ({ ...integration.values }));
  // Track which fields the user actually edited, so we only send those.
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const confirm = useConfirm();

  const hasHelp = Boolean(INTEGRATION_HELP[integration.key]);

  // Anything with a stored row can be cleared — including a row that no longer
  // decrypts. Gating on `source === 'db'` (as this used to) hid the button on
  // exactly the cards that needed it most: after the encryption secret rotated,
  // `source` falls back to 'env'/'none', the card claims to be unconfigured, and
  // the dead ciphertext stays in the table with no way to remove it.
  const canClear = integration.hasStoredCredentials;
  // A row exists but the active config isn't coming from it.
  const undecryptable = integration.hasStoredCredentials && integration.source !== 'db';

  const style = STATUS_STYLES[integration.status];
  const StatusIcon = style.icon;

  function setField(key: string, value: string) {
    setValues((v) => ({ ...v, [key]: value }));
    setDirty((d) => ({ ...d, [key]: true }));
    setTestResult(null);
  }

  // Build the config payload: include only edited fields, and never send the
  // literal mask (the backend treats blank/mask as "keep stored secret").
  const payload = useMemo<ConfigInput>(() => {
    const out: ConfigInput = {};
    for (const field of integration.fields) {
      if (!dirty[field.key]) continue;
      const raw = values[field.key] ?? '';
      if (field.secret && (raw === '' || raw === MASK)) continue;
      if (field.type === 'boolean') {
        out[field.key] = raw === 'true' || raw === 'on' || raw === '1';
      } else if (field.type === 'number') {
        const n = Number(raw);
        out[field.key] = Number.isFinite(n) ? n : raw;
      } else {
        out[field.key] = raw;
      }
    }
    return out;
  }, [dirty, values, integration.fields]);

  async function handleTest() {
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      const result = await testIntegration(integration.key, payload, companyId);
      setTestResult(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Testul a eșuat.');
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const updated = await saveIntegration(integration.key, payload, companyId);
      onChanged(updated);
      setValues({ ...updated.values });
      setDirty({});
      if (updated.lastTestResult) {
        setTestResult({ ok: updated.isConnected, message: updated.lastTestResult });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Salvarea a eșuat.');
    } finally {
      setSaving(false);
    }
  }

  async function handleClear() {
    if (
      !(await confirm({
        message: `Ștergeți credențialele salvate pentru ${integration.label}?`,
        danger: true,
      }))
    )
      return;
    setClearing(true);
    setError(null);
    setTestResult(null);
    try {
      // One request that drops the whole row. Field-by-field deletion could fail
      // halfway and leave, say, the SMTP password stored — the worst possible
      // outcome for "delete my credentials" — and could never clear a row that
      // no longer decrypts. Resolution falls back to .env afterwards (if any).
      const latest = await deleteIntegration(integration.key, companyId);
      onChanged(latest);
      setValues({ ...latest.values });
      setDirty({});
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Ștergerea a eșuat.');
    } finally {
      setClearing(false);
    }
  }

  // The key comes with the customer's Firmio subscription: nothing to edit here.
  if (integration.managedByFirmio) {
    return (
      <section className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{integration.label}</h3>
            <p className="mt-1 text-sm text-gray-500">{integration.description}</p>
          </div>
          <span className="shrink-0 rounded-full border border-emerald-500/40 px-2.5 py-0.5 text-xs font-medium text-emerald-600">
            Inclus în abonamentul Firmio
          </span>
        </div>
        <p className="mt-3 text-sm text-gray-600">
          Cheia API e furnizată de Firmio, împreună cu limita lunară de utilizare. Pentru
          modificări, contactați Firmio.
        </p>
      </section>
    );
  }

  return (
    <section className={`rounded-lg border p-5 ${style.container}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`rounded-md p-2 ${style.iconWrap}`}>
            <StatusIcon className={`h-5 w-5 ${style.iconClass}`} />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <h3 className="text-sm font-semibold text-gray-900">{integration.label}</h3>
              {hasHelp && (
                <button
                  type="button"
                  onClick={() => setHelpOpen(true)}
                  aria-label={`Cum configurezi ${integration.label}`}
                  title="Cum obții aceste date? Ghid pas cu pas"
                  className="rounded-full p-0.5 text-gray-400 hover:bg-brand-50 hover:text-brand-600"
                >
                  <Info className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500">{integration.description}</p>
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-medium ${style.badge}`}
        >
          {style.label}
        </span>
      </div>

      {helpOpen && (
        <IntegrationHelpModal
          integrationKey={integration.key}
          label={integration.label}
          onClose={() => setHelpOpen(false)}
        />
      )}

      {/* Credential fields */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {integration.fields.map((field) => {
          // Platform-managed values (OAuth tokens) have no input: the human
          // never types them, the callback writes them.
          if (field.platform) return null;
          const id = `${integration.key}-${field.key}`;
          const value = values[field.key] ?? '';
          if (field.type === 'boolean') {
            return (
              <label key={field.key} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  id={id}
                  type="checkbox"
                  checked={value === 'true'}
                  onChange={(e) => setField(field.key, e.target.checked ? 'true' : 'false')}
                  className="h-4 w-4 rounded border-gray-300 text-brand-600"
                />
                {field.label}
              </label>
            );
          }
          if (field.type === 'select') {
            return (
              <div key={field.key}>
                <span className="text-xs font-medium text-gray-600">
                  {field.label}
                  {field.required && <span className="ml-0.5 text-red-600">*</span>}
                </span>
                <Select
                  label={field.label}
                  hideLabel
                  value={value || field.options?.[0]?.value || ''}
                  onChange={(v) => setField(field.key, v)}
                  options={field.options ?? []}
                  className="mt-1"
                />
              </div>
            );
          }
          const common = {
            id,
            value,
            placeholder: field.placeholder,
            onFocus: () => {
              // Clear the mask on focus so the user can type a new secret.
              if (field.secret && value === MASK) setField(field.key, '');
            },
            className:
              'mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
          };
          return (
            <div key={field.key} className={field.type === 'textarea' ? 'sm:col-span-2' : ''}>
              <label htmlFor={id} className="text-xs font-medium text-gray-600">
                {field.label}
                {field.required && <span className="ml-0.5 text-red-600">*</span>}
              </label>
              {field.type === 'textarea' ? (
                <textarea
                  {...common}
                  rows={4}
                  onChange={(e) => setField(field.key, e.target.value)}
                />
              ) : (
                <input
                  {...common}
                  type={field.secret ? 'password' : field.type === 'number' ? 'number' : 'text'}
                  onChange={(e) => setField(field.key, e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Test result / error */}
      {testResult && (
        <p
          className={`mt-3 flex items-center gap-1.5 text-sm ${
            testResult.ok ? 'text-emerald-500' : 'text-red-500'
          }`}
        >
          {testResult.ok ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          {testResult.ok ? `Conectat cu succes — ${testResult.message}` : testResult.message}
        </p>
      )}
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      {integration.lastTestedAt && !testResult && (
        <p className="mt-3 text-xs text-gray-500">
          Ultima verificare: {formatTimestamp(integration.lastTestedAt)}
          {integration.lastTestResult ? ` — ${integration.lastTestResult}` : ''}
        </p>
      )}
      {/* A stored row that isn't the active config can only mean it failed to
          decrypt. Without saying so the card just looks unconfigured and the
          user never learns why the integration stopped working. */}
      {undecryptable && (
        <p className="mt-3 flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-600">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Există credențiale salvate care nu pot fi decriptate (secretul de criptare s-a
            schimbat). Șterge-le și reintrodu-le.
          </span>
        </p>
      )}
      {integration.source === 'env' && (
        <p className="mt-2 text-xs text-gray-400">
          Configurat momentan din .env. Salvați aici pentru a stoca în baza de date (criptat).
          {!integration.hasStoredCredentials &&
            ' Nu există nimic stocat de șters — valorile vin din variabilele de mediu ale serverului.'}
        </p>
      )}

      {/* Actions */}
      {/* On a phone the three buttons do not fit on one line: let them wrap,
          and push the destructive one to the right only when there is room. */}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
        <button
          type="button"
          onClick={handleTest}
          disabled={testing || saving || clearing}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          {testing && <Loader2 className="h-4 w-4 animate-spin" />}
          Testează conexiunea
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || testing || clearing}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-40"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Salvează
        </button>
        {canClear && (
          <button
            type="button"
            onClick={handleClear}
            disabled={clearing || saving || testing}
            className="inline-flex items-center gap-1.5 rounded-md border border-red-300 sm:ml-auto px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
          >
            {clearing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Șterge credențialele salvate
          </button>
        )}
      </div>
    </section>
  );
}
